import type * as hast from 'hast';

import {
    BLOCK_CODE,
    BLOCK_CODE_DEFAULT_OVERFLOW,
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_CONTENT_REF,
    BLOCK_DIVIDER,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_MATH,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    type BlockEmbedData,
    type BlockImageData,
    type BlockImagesData,
    type BlockOpenAPIOperationData,
    type BlockOpenAPISchemasData,
    type BlockOpenAPIWebhookData,
    FRAGMENT_CAPTION,
    FRAGMENT_EXPANDABLE_BODY,
    FRAGMENT_EXPANDABLE_TITLE,
    type InlineImageData,
    images,
    lengthToString,
} from '@gitbook/doc-core';
import { Block, Inline } from '@gitbook/slate';

import type { FormattingContext } from '../context';
import { transformBlocksIntegration } from './transform-blocks-integration';
import { transformBlocksSwagger } from './transform-blocks-swagger';
import { transformBlocksTable } from './transform-blocks-table';
import type { GastToHastTransformFn, GastToHastTransformMap } from './types';
import { createCustomBlock } from './utils';

export const transformBlocks: GastToHastTransformMap<Block> = {
    ...transformBlocksTable,
    ...transformBlocksSwagger,
    ...transformBlocksIntegration,
    [BLOCK_OPENAPI_OPERATION]: async (node, _eat, options) => {
        const data = node.data.toFrozenObject<BlockOpenAPIOperationData>();

        const resolved = await options.resolveContentRef(data.ref);
        if (!resolved) {
            return [];
        }

        return [
            createCustomBlock(
                BLOCK_OPENAPI_OPERATION,
                {
                    spec: data.ref.spec,
                    path: data.path,
                    method: data.method,
                },
                [
                    {
                        type: 'element',
                        tagName: 'a',
                        properties: {
                            href: resolved.url,
                        },
                        children: [
                            {
                                type: 'text',
                                value: resolved.text,
                            },
                        ],
                    },
                ]
            ),
        ];
    },
    [BLOCK_OPENAPI_SCHEMAS]: async (node, _eat, options) => {
        const data = node.data.toFrozenObject<BlockOpenAPISchemasData>();

        const resolved = await options.resolveContentRef(data.ref);
        if (!resolved) {
            return [];
        }

        return [
            createCustomBlock(
                BLOCK_OPENAPI_SCHEMAS,
                {
                    spec: data.ref.spec,
                    schemas: data.schemas.join(','),
                    grouped: data.grouped,
                },
                [
                    {
                        type: 'element',
                        tagName: 'a',
                        properties: {
                            href: resolved.url,
                        },
                        children: [
                            {
                                type: 'text',
                                value: resolved.text,
                            },
                        ],
                    },
                ]
            ),
        ];
    },
    [BLOCK_OPENAPI_WEBHOOK]: async (node, _eat, options) => {
        const data = node.data.toFrozenObject<BlockOpenAPIWebhookData>();

        const resolved = await options.resolveContentRef(data.ref);
        if (!resolved) {
            return [];
        }

        return [
            createCustomBlock(
                BLOCK_OPENAPI_WEBHOOK,
                {
                    spec: data.ref.spec,
                    name: data.name,
                    method: data.method,
                },
                [
                    {
                        type: 'element',
                        tagName: 'a',
                        properties: {
                            href: resolved.url,
                        },
                        children: [
                            {
                                type: 'text',
                                value: resolved.text,
                            },
                        ],
                    },
                ]
            ),
        ];
    },

    [BLOCK_HEADING_1]: createHeadingTransform(1),
    [BLOCK_HEADING_2]: createHeadingTransform(2),
    [BLOCK_HEADING_3]: createHeadingTransform(3),
    [BLOCK_PARAGRAPH]: async (node, eat) => {
        const align = node.data.get('align');
        return [
            {
                type: 'element',
                tagName: 'p',
                children: await eat(node.nodes),
                properties:
                    align && align !== 'start' ? { align: align === 'end' ? 'right' : align } : {},
            },
        ];
    },
    [BLOCK_DIVIDER]: async () => {
        return [{ type: 'element', tagName: 'hr', properties: {}, children: [] }];
    },
    [BLOCK_QUOTE]: async (node, eat) => {
        return [
            {
                type: 'element',
                tagName: 'blockquote',
                children: await eat(node.nodes),
                properties: {},
            },
        ];
    },
    [BLOCK_HINT]: async (node, eat) => {
        const style = node.data.get('style');
        return [
            createCustomBlock(
                BLOCK_HINT,
                {
                    style,
                    className: `hint hint-${style}`,
                },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_IF]: async (node, eat) => {
        return [
            createCustomBlock(
                BLOCK_IF,
                { expression: node.data.get('expression') },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_CODE]: async (node, eat) => {
        const data = node.data.toFrozenObject();
        const lines: Array<hast.Element | hast.Text> = [];
        const lineBlocks = node.nodes.toArray() as Block[];

        for (let index = 0; index < lineBlocks.length; index++) {
            const line = lineBlocks[index];
            const lineData = line.data.toFrozenObject();
            const lineChildren = await eat(line.nodes);
            lineChildren.push({ type: 'text', value: '\n' });

            if (lineData.highlighted) {
                lines.push({
                    type: 'element',
                    tagName: 'strong',
                    children: lineChildren,
                    properties: {},
                });
            } else {
                lines.push(...lineChildren);
            }
        }

        return [
            {
                type: 'element',
                tagName: 'pre',
                properties: {
                    ...(node.data.has('syntax')
                        ? { className: `language-${node.data.get('syntax')}` }
                        : {}),
                    ...(typeof data.title === 'string' ? { dataTitle: data.title } : {}),
                    ...(data.overflow && data.overflow !== BLOCK_CODE_DEFAULT_OVERFLOW
                        ? { dataOverflow: data.overflow }
                        : {}),
                    ...(data.lineNumbers ? { dataLineNumbers: data.lineNumbers } : {}),
                    ...(typeof data.fullWidth !== 'undefined'
                        ? { dataFullWidth: `${Boolean(data.fullWidth)}` }
                        : undefined),
                },
                children: [
                    {
                        type: 'element',
                        tagName: 'code',
                        properties: {
                            ...(node.data.has('syntax')
                                ? { className: `lang-${node.data.get('syntax')}` }
                                : {}),
                        },
                        children: lines,
                    },
                ],
            },
        ];
    },
    [BLOCK_EMBED]: async (node, eat) => {
        const caption = node.fragments.get(FRAGMENT_CAPTION);
        const data = node.data.toFrozenObject() as BlockEmbedData;

        const captionElement: hast.Element | null =
            caption && !caption.isEmpty
                ? {
                      type: 'element',
                      tagName: 'div',
                      properties: {
                          className: 'gb-ignore caption',
                      },
                      children: await eat(caption.nodes),
                  }
                : null;

        return [
            createCustomBlock(
                BLOCK_EMBED,
                {
                    ...(typeof data.fullWidth !== 'undefined'
                        ? { fullWidth: `${Boolean(data.fullWidth)}` }
                        : undefined),
                },
                [
                    {
                        type: 'element',
                        tagName: 'iframe',
                        properties: {
                            src: data.url,
                        },
                        children: [],
                    },
                    ...(captionElement ? [captionElement] : []),
                ]
            ),
        ];
    },
    [BLOCK_COLUMNS]: async (node, eat) => {
        const fullWidth = node.data.get('fullWidth');
        return [
            createCustomBlock(
                BLOCK_COLUMNS,
                {
                    ...(typeof fullWidth !== 'undefined'
                        ? { fullWidth: `${Boolean(fullWidth)}` }
                        : undefined),
                },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_COLUMN]: async (node, eat) => {
        const width = node.data.get('width');
        const verticalAlignment = node.data.get('verticalAlignment');
        return [
            createCustomBlock(
                BLOCK_COLUMN,
                {
                    ...(typeof width !== 'undefined' ? { width: lengthToString(width) } : {}),
                    ...(typeof verticalAlignment !== 'undefined'
                        ? { valign: verticalAlignment }
                        : {}),
                },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_TABS]: async (node, eat) => {
        const fullWidth = node.data.get('fullWidth');
        return [
            createCustomBlock(
                BLOCK_TABS,
                {
                    ...(typeof fullWidth !== 'undefined'
                        ? { fullWidth: `${Boolean(fullWidth)}` }
                        : undefined),
                },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_TABS_ITEM]: async (node, eat) => {
        const title = node.data.get('title');
        return [
            createCustomBlock(
                'tab',
                {
                    title,
                },
                await eat(node.nodes)
            ),
        ];
    },
    [BLOCK_STEPPER]: async (node, eat) => {
        return [createCustomBlock('stepper', {}, await eat(node.nodes))];
    },
    [BLOCK_STEPPER_STEP]: async (node, eat) => {
        return [createCustomBlock('step', {}, await eat(node.nodes))];
    },
    [BLOCK_LIST_OL]: async (node, eat) => {
        const data = node.data.toFrozenObject();

        return [
            {
                type: 'element',
                tagName: 'ol',
                children: await eat(node.nodes),
                properties: data.start
                    ? {
                          start: data.start,
                      }
                    : {},
            },
        ];
    },
    [BLOCK_LIST_UL]: async (node, eat) => {
        return [
            {
                type: 'element',
                tagName: 'ul',
                children: await eat(node.nodes),
                properties: {},
            },
        ];
    },
    [BLOCK_LIST_TASKS]: async (node, eat) => {
        return [
            {
                type: 'element',
                tagName: 'ul',
                properties: {
                    className: 'contains-task-list',
                },
                children: await eat(node.nodes),
            },
        ];
    },
    [BLOCK_LIST_ITEM]: async (node, eat) => {
        const firstChild = node.nodes.size === 1 && node.nodes.get(0);

        // List items don't need a paragraph (if it's the only element).
        const children =
            firstChild && firstChild.object === 'block' && firstChild.type === BLOCK_PARAGRAPH
                ? await eat(firstChild.nodes)
                : await eat(node.nodes);

        const checkboxElement: hast.Element | null = node.data.has('checked')
            ? {
                  type: 'element',
                  tagName: 'input',
                  properties: {
                      type: 'checkbox',
                      checked: node.data.get('checked'),
                  },
                  children: [],
              }
            : null;

        return [
            {
                type: 'element',
                tagName: 'li',
                children: [...(checkboxElement ? [checkboxElement] : []), ...children],
                properties: {},
            },
        ];
    },
    [BLOCK_MATH]: (node) => {
        const formula = node.data.get('formula');

        return [
            {
                type: 'element',
                tagName: 'div',
                properties: {
                    className: ['math', 'math-display'],
                },
                children: [
                    {
                        type: 'text',
                        value: formula,
                    },
                ],
            },
        ];
    },
    [BLOCK_IMAGE]: async (node, eat, options) => {
        return createImage(
            node,
            async () => {
                const caption = node.fragments.get(FRAGMENT_CAPTION);
                if (!caption || caption.isEmpty) {
                    return [];
                }

                return eat(caption.nodes);
            },
            options
        );
    },
    [BLOCK_IMAGES]: async (node, eat) => {
        const data = node.data.toFrozenObject<BlockImagesData>();

        const alignProp = typeof data.align === 'string' ? { align: data.align } : undefined;
        const fullWidthProp =
            typeof data.fullWidth !== 'undefined'
                ? { dataFullWidth: `${Boolean(data.fullWidth)}` }
                : undefined;
        const withFrameProp =
            typeof data.withFrame !== 'undefined'
                ? { dataWithFrame: `${Boolean(data.withFrame)}` }
                : undefined;

        const children = await eat(node.nodes);
        /**
         * If at least one figure is present in the images block or there's an explicit align,
         * fullWidth or withFrame prop, we have to wrap it in a "div" to make it valid HTML
         */
        return [
            children.some((child) => child.type === 'element' && child.tagName === 'figure') ||
            alignProp ||
            fullWidthProp ||
            withFrameProp
                ? {
                      type: 'element',
                      tagName: 'div',
                      properties: {
                          ...alignProp,
                          ...fullWidthProp,
                          ...withFrameProp,
                      },
                      children,
                  }
                : {
                      type: 'element',
                      tagName: 'p',
                      children,
                      properties: {},
                  },
        ];
    },
    [BLOCK_FILE]: async (node, eat, options) => {
        const caption = node.fragments.get(FRAGMENT_CAPTION);
        const resolved = await options.resolveContentRef(node.data.get('ref'));
        if (!resolved) {
            return [];
        }
        return [
            createCustomBlock(
                BLOCK_FILE,
                {
                    src: resolved.url,
                },
                caption && !caption.isEmpty ? await eat(caption.nodes) : []
            ),
        ];
    },
    [BLOCK_DRAWING]: async (node, _eat, options) => {
        const caption = node.fragments.get(FRAGMENT_CAPTION);
        const ref = node.data.get('ref');
        const resolved = ref ? await options.resolveContentRef(ref) : undefined;

        return [
            {
                type: 'element',
                tagName: 'img',
                properties: {
                    src: resolved?.url,
                    alt: caption?.text || '',
                    class: 'gitbook-drawing',
                },
                children: [],
            },
        ];
    },
    [BLOCK_CONTENT_REF]: async (node, _eat, options) => {
        const ref = node.data.get('ref');
        const resolved = await options.resolveContentRef(ref);
        if (!resolved) {
            return [];
        }

        return [
            createCustomBlock(
                BLOCK_CONTENT_REF,
                {
                    url: resolved.url,
                },
                [
                    {
                        type: 'element',
                        tagName: 'a',
                        properties: {
                            href: resolved.url,
                        },
                        children: [
                            {
                                type: 'text',
                                value: resolved.text,
                            },
                        ],
                    },
                ]
            ),
        ];
    },
    [BLOCK_REUSABLE_CONTENT]: async (node, _eat, options) => {
        const ref = node.data.get('ref');
        const resolved = await options.resolveContentRef(ref);
        if (!resolved) {
            return [];
        }

        return [
            createCustomBlock(
                BLOCK_REUSABLE_CONTENT,
                {
                    url: resolved.url,
                },
                [
                    {
                        type: 'element',
                        tagName: 'a',
                        properties: {
                            href: resolved.url,
                        },
                        children: [
                            {
                                type: 'text',
                                value: resolved.text,
                            },
                        ],
                    },
                ]
            ),
        ];
    },
    [BLOCK_EXPANDABLE]: async (node, eat) => {
        const title = node.getFragmentByKey(FRAGMENT_EXPANDABLE_TITLE)!;
        const body = node.getFragmentByKey(FRAGMENT_EXPANDABLE_BODY)!;

        return [
            {
                type: 'element',
                tagName: 'details',
                properties: {},
                children: [
                    {
                        type: 'element',
                        tagName: 'summary',
                        properties: {},
                        children: [...(await eat(title.nodes.first()!.nodes))],
                    },
                    ...(await eat(body.nodes)),
                ],
            },
        ];
    },
};

/**
 * Create a heading transform.
 */
function createHeadingTransform(depth: number): GastToHastTransformFn<Block> {
    return async (node, eat, options) => {
        const id = node.data.get('id', '');
        const align = node.data.get('align');
        const props: Record<string, string> = {};
        if (id) {
            props.id = id;
        }
        if (align && align !== 'start') {
            props.align = align === 'end' ? 'right' : align;
        }
        return [
            {
                type: 'element',
                tagName: `h${depth + (options.headingsOffset || 0)}`,
                children: await eat(node.nodes),
                properties: props,
            },
        ];
    };
}

/**
 * Create an image node.
 */
export async function createImage(
    node: Block | Inline,
    getBlockCaption: () => Promise<(hast.Element | hast.Text)[]>,
    context: FormattingContext
): Promise<hast.Element[]> {
    const imageData = node.data.toFrozenObject() as InlineImageData | BlockImageData;
    const height = images.getImageHeight(node);
    const width = images.getImageWidth(node);
    const size = Block.isBlock(node) ? undefined : node.data.get('size', '');

    const [resolved, resolvedDark] = await Promise.all([
        context.resolveContentRef(imageData.ref),
        imageData.refDark ? context.resolveContentRef(imageData.refDark) : null,
    ]);

    // If no image is resolved, we don't output anything.
    if (!resolved) {
        return [];
    }

    // If no alt data then it's a node from before where alt = caption
    const imageAlt = images.getImageAltText(node);

    let alt = imageAlt;
    if (typeof alt === 'undefined') {
        const caption = Block.isBlock(node) ? node.fragments.get(FRAGMENT_CAPTION) : null;
        alt = caption?.text || '';
    }

    let imgElement: hast.Element = {
        type: 'element',
        tagName: 'img',
        properties: {
            src: resolved.url,
            alt: alt || '',
            ...(size ? { 'data-size': size } : {}),
            ...(height ? { height } : {}),
            ...(width ? { width } : {}),
        },
        children: [],
    };

    // Compute the alternative sources.
    if (resolvedDark) {
        imgElement = {
            type: 'element',
            tagName: 'picture',
            properties: {},
            children: [
                {
                    type: 'element',
                    tagName: 'source',
                    properties: {
                        srcset: resolvedDark.url,
                        media: '(prefers-color-scheme: dark)',
                    },
                    children: [],
                },
                imgElement,
            ],
        };
    }

    // If no alt data on the image node then it's a node from before where alt = caption
    const isNoAltLegacyImage = typeof imageAlt === 'undefined';

    // We only generate a <img> if there is nothing to put in a `<picture>` or `<figure>`.
    if (Inline.isInline(node) || isNoAltLegacyImage) {
        return [imgElement];
    }

    // Block images that have `alt` are always represented as a `<figure>`.
    // even if there is no caption.
    // This is to ensure we can correctly parse legayc images where `alt` attribute was representing the caption.
    const captionElements = await getBlockCaption();
    return [
        {
            type: 'element',
            tagName: 'figure',
            properties: {},
            children: [
                imgElement,
                {
                    type: 'element',
                    tagName: 'figcaption',
                    properties: {},
                    children: captionElements,
                },
            ],
        },
    ];
}
